import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Swords, Trophy, Coins, Crown, BadgeCheck, Gem } from "lucide-react";

function StatCard({ label, value, Icon }) {
  return (
    <div className="card-surface p-3 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-rose-600/15 border border-rose-600/30 text-rose-400 flex items-center justify-center shrink-0">
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="font-display text-xl truncate">{value}</div>
      </div>
    </div>
  );
}

function BattleRow({ battle, viewerHandle }) {
  const meIsHost = battle.host?.id === viewerHandle;
  const me = meIsHost ? battle.host : battle.opponent;
  const opp = meIsHost ? battle.opponent : battle.host;
  const myScore = meIsHost ? battle.host_score : battle.opponent_score;
  const oppScore = meIsHost ? battle.opponent_score : battle.host_score;
  const isWinner = battle.winner_id === me?.id;
  const isDeclined = battle.status === "declined";
  const isActive = battle.status === "active";

  return (
    <div
      className={`card-surface p-4 flex items-center gap-3 ${
        isWinner ? "border-l-2 border-l-yellow-400" : ""
      }`}
      data-testid={`profile-battle-${battle.id}`}
    >
      <div className="flex items-center gap-2 shrink-0">
        <Avatar className="w-9 h-9 border border-white/10">
          <AvatarImage src={me?.avatar_url} />
          <AvatarFallback className="bg-rose-600 text-white text-xs">
            {me?.display_name?.[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="text-lg font-display">{myScore}</div>
      </div>
      <div className="text-xs uppercase tracking-widest text-zinc-500">vs</div>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="text-lg font-display">{oppScore}</div>
        <Avatar className="w-9 h-9 border border-white/10">
          <AvatarImage src={opp?.avatar_url} />
          <AvatarFallback className="bg-zinc-700 text-white text-xs">
            {opp?.display_name?.[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <Link
            to={`/u/${opp?.id}`}
            className="text-sm text-white truncate hover:text-rose-400 block"
          >
            {opp?.display_name}
          </Link>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {new Date(battle.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>
      <div className="text-right shrink-0">
        {isDeclined ? (
          <span className="text-[10px] uppercase tracking-widest bg-zinc-800 text-zinc-400 rounded-full px-2 py-0.5">
            Declined
          </span>
        ) : isActive ? (
          <span className="text-[10px] uppercase tracking-widest bg-rose-600 text-white rounded-full px-2 py-0.5">
            Live now
          </span>
        ) : isWinner ? (
          <span className="text-[10px] uppercase tracking-widest bg-yellow-400 text-black rounded-full px-2 py-0.5 flex items-center gap-1">
            <Crown size={10} /> Win
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest bg-white/5 text-zinc-400 rounded-full px-2 py-0.5">
            Loss
          </span>
        )}
      </div>
    </div>
  );
}

export default function ProfileBattles({ handle }) {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [window, setWindow] = useState("all");
  const [loading, setLoading] = useState(true);
  const [champion, setChampion] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!handle) return;
    setLoading(true);
    Promise.all([
      api.get(`/users/${handle}/battles`).then((r) => r.data).catch(() => null),
      api.get(`/leaderboard/battles`, { params: { window } }).then((r) => r.data.leaderboard).catch(() => []),
      api.get(`/battles/champion/current`).then((r) => r.data).catch(() => null),
      api.get(`/users/${handle}`).then((r) => r.data).catch(() => null),
    ]).then(([d, b, ch, p]) => {
      setData(d);
      setBoard(b || []);
      setChampion(ch);
      setProfile(p);
      setLoading(false);
    });
  }, [handle, window]);

  const isChampionProfile = !!(
    profile?.battle_champion_expires_at &&
    new Date(profile.battle_champion_expires_at) > new Date()
  );

  if (loading) return <div className="text-zinc-500 text-sm">Loading battles…</div>;
  if (!data || (data.stats.battles === 0 && (board?.length || 0) === 0)) {
    return (
      <div className="card-surface p-6 text-center" data-testid="profile-battles-empty">
        <Swords size={32} className="text-zinc-600 mx-auto mb-3" />
        <div className="text-sm text-zinc-500">
          No battles yet. Go live, challenge someone, win coins.
        </div>
      </div>
    );
  }

  const myRank = board.findIndex((row) => row.user.id === profile?.id);
  const s = data.stats;

  return (
    <div className="space-y-6" data-testid="profile-battles">
      {isChampionProfile && (
        <div
          className="relative overflow-hidden rounded-2xl border border-yellow-400/40 bg-gradient-to-r from-yellow-500/15 via-amber-500/10 to-rose-500/15 p-4 flex items-center gap-4"
          data-testid="battle-champion-banner"
        >
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center shadow-lg shadow-yellow-500/40 shrink-0">
            <Crown className="text-black" size={26} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300 mb-0.5">
              Battle Champion · {profile?.battle_champion_month}
            </div>
            <div className="font-display text-xl text-white truncate">
              {profile?.display_name || "This member"} won this month's crown 👑
            </div>
            <div className="text-[11px] text-zinc-400 mt-0.5">
              Gold badge active until{" "}
              {new Date(profile.battle_champion_expires_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      )}

      {champion && champion.user?.id !== profile?.id && (
        <div
          className="card-surface p-3 flex items-center gap-3 border-l-2 border-l-yellow-400"
          data-testid="current-champion-callout"
        >
          <Crown className="text-yellow-400" size={18} />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Avatar className="w-8 h-8 border border-white/10">
              <AvatarImage src={champion.user.avatar_url} />
              <AvatarFallback className="bg-rose-600 text-white text-xs">
                {champion.user.display_name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-sm text-white truncate">
                {champion.user.display_name} · {champion.wins} wins this month
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                Reigning Battle Champion
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Wins" value={s.wins} Icon={Trophy} />
        <StatCard label="Losses" value={s.losses} Icon={Swords} />
        <StatCard label="Win rate" value={`${s.win_rate}%`} Icon={Crown} />
        <StatCard label="Coins earned" value={s.coins_earned.toLocaleString()} Icon={Coins} />
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
          Battle history
        </div>
        <div className="space-y-2">
          {data.history.length === 0 ? (
            <div className="text-sm text-zinc-500 card-surface p-4 text-center">
              No completed battles yet.
            </div>
          ) : (
            data.history.map((b) => (
              <BattleRow key={b.id} battle={b} viewerHandle={profile?.id} />
            ))
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">
            Battle leaderboard <span className="text-zinc-600 normal-case tracking-normal" data-testid="leaderboard-ranked-by">· ranked by diamonds</span>
          </div>
          <div className="flex items-center gap-1">
            {[
              { v: "7d", label: "7d" },
              { v: "30d", label: "30d" },
              { v: "all", label: "All-time" },
            ].map((w) => (
              <button
                key={w.v}
                onClick={() => setWindow(w.v)}
                data-testid={`leaderboard-window-${w.v}`}
                className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                  window === w.v
                    ? "bg-rose-600 border-rose-600 text-white"
                    : "border-white/10 text-zinc-400 hover:text-white"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        {board.length === 0 ? (
          <div className="text-sm text-zinc-500 card-surface p-4 text-center">
            No battles ended in this window.
          </div>
        ) : (
          <div className="card-surface divide-y divide-white/5">
            {board.map((row, i) => {
              const isMe = row.user.id === profile?.id;
              return (
                <Link
                  key={row.user.id}
                  to={`/u/${row.user.id}`}
                  data-testid={`leaderboard-row-${row.user.id}`}
                  className={`flex items-center gap-3 p-3 hover:bg-white/5 transition ${
                    isMe ? "bg-yellow-400/5" : ""
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-display text-sm shrink-0 ${
                      i === 0
                        ? "bg-yellow-400 text-black"
                        : i === 1
                        ? "bg-zinc-300 text-black"
                        : i === 2
                        ? "bg-amber-700 text-white"
                        : "bg-white/5 text-zinc-400 border border-white/10"
                    }`}
                  >
                    {i + 1}
                  </div>
                  <Avatar className="w-9 h-9 border border-white/10">
                    <AvatarImage src={row.user.avatar_url} />
                    <AvatarFallback className="bg-rose-600 text-white text-xs">
                      {row.user.display_name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 text-sm text-white">
                      <span className="truncate">{row.user.display_name}</span>
                      {row.user.is_verified && (
                        <BadgeCheck size={12} className="text-amber-400 shrink-0" />
                      )}
                      {champion?.user?.id === row.user.id && (
                        <Crown
                          size={12}
                          className="text-yellow-400 shrink-0"
                          data-testid="leaderboard-crown"
                        />
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{row.user.display_name}</div>
                  </div>
                  <div className="text-right shrink-0" data-testid={`leaderboard-row-${row.user.id}`}>
                    <div className="font-display text-lg flex items-center justify-end gap-1" data-testid="leaderboard-diamonds">
                      <Gem size={13} className="text-sky-300" /> {(row.diamonds ?? row.coins_earned ?? 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      {row.wins} {row.wins === 1 ? "win" : "wins"}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        {myRank >= 0 && (
          <div className="text-[11px] text-yellow-300 mt-2 text-center" data-testid="my-rank">
            You're ranked #{myRank + 1}
          </div>
        )}
      </div>
    </div>
  );
}
